import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { RowDataDb } from 'src/app/model/help';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as HelpActions from 'src/app/store/help/help.actions';
import { selectRowModify } from 'src/app/store/help/help.selector';

@Component({
  templateUrl: './helper-add-row.dialog.component.html',
})
export class HelperAddRowDialogComponent implements OnInit, OnDestroy {

  rowModifySuccess$: Observable<boolean | undefined>;
  rowModifySuccessSub: Subscription | undefined;


  rowForm: FormGroup;
  row: RowDataDb;
  adding = true;

  subsectionId: number;

  // design
  screenWidth: number;
  MIN_MD_WIDTH = 500;
  MAX_MD_WIDTH = 1120;
  MIN_WIDTH_COL4 = 1680;
  DELTA_ERROR_PIXEL = 7;
  MIN_TWO_BUTTONS_DIALOG_WIDTH = 575 + this.DELTA_ERROR_PIXEL;

  MIN_MD_WIDTH_EDIT = 600 + this.DELTA_ERROR_PIXEL;
  MIN_EXTENDED_INPUTTEXT = 750;

  types = [
    { name: 'TEXT', code: 'text' },
    { name: 'VIDEO', code: 'video' },
    { name: 'IMAGE', code: 'image' },
  ];

  constructor(
    private store: Store<AppState>,
    private ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {
    if (config.data !== undefined && config.data.subsectionId !== undefined) {
      this.subsectionId = config.data.subsectionId;
    }
    if (config.data !== undefined && config.data.row !== undefined) {
      this.row = config.data.row;
      this.adding = false;
    } else {
      this.row = new RowDataDb();
      this.row.Order = 0;
      this.row.ContentType = 'text';
      this.row.StartSecond = 0;
      this.row.EndSecond = 0;
      this.adding = true;
    }
    this.initSelectors();
  }



  ngOnInit(): void {
    this.subscribeAll();
    this.buildForm();
    this.onResize();
  }

  buildForm(): void {
    this.rowForm = new FormGroup({
      'ContentType': new FormControl(this.row.ContentType, Validators.required),
      'ContentIt': new FormControl(this.row.ContentIt, Validators.required),
      'ContentEn': new FormControl(this.row.ContentEn, Validators.required),
      'ContentEs': new FormControl(this.row.ContentEs, Validators.required),
      'ContentDe': new FormControl(this.row.ContentDe, Validators.required),
      'ContentFr': new FormControl(this.row.ContentFr, Validators.required),
      'ContentNl': new FormControl(this.row.ContentNl, Validators.required),
      'ContentPl': new FormControl(this.row.ContentPl, Validators.required),
      'ContentRu': new FormControl(this.row.ContentRu, Validators.required),
      'ContentSk': new FormControl(this.row.ContentSk, Validators.required),
      'StartSecond': new FormControl(this.row.StartSecond, Validators.required),
      'EndSecond': new FormControl(this.row.EndSecond, Validators.required),
    });
  }

  initSelectors(): void {
    this.rowModifySuccess$ = this.store.select(selectRowModify);
  }


  subscribeAll(): void {
    this.rowModifySuccessSub = this.rowModifySuccess$?.subscribe(
      (rowModifySuccess) => {
        if (rowModifySuccess) {
          this.ref.close('success');
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.rowModifySuccessSub?.unsubscribe();
  }

  isRowText(): boolean {
    return this.f.ContentType.value === 'text';
  }
  isRowVideo(): boolean {
    return this.f.ContentType.value === 'video';
  }


  submit(): void {
    if (this.subsectionId === undefined) {
      this.subsectionId = this.row.SubsectionId;
    }
    this.row = {
      ...this.row,
      SubsectionId: this.subsectionId,
      ContentType: this.f.ContentType.value,
      ContentIt: this.f.ContentIt.value,
      ContentEn: this.f.ContentEn.value,
      ContentEs: this.f.ContentEs.value,
      ContentDe: this.f.ContentDe.value,
      ContentFr: this.f.ContentFr.value,
      ContentNl: this.f.ContentNl.value,
      ContentPl: this.f.ContentPl.value,
      ContentRu: this.f.ContentRu.value,
      ContentSk: this.f.ContentSk.value,
      StartSecond: this.f.StartSecond.value,
      EndSecond: this.f.EndSecond.value,
    };

    if (this.row.Id) {
      this.store.dispatch(HelpActions.editHelpRow({ row: this.row }));
    } else {
      this.store.dispatch(HelpActions.addHelpRow({ row: this.row }));
    }
  }

  get f(): any {
    return this.rowForm.controls;
  }

  closeDialog(): void {
    this.ref.close();
  }

  // Design
  @HostListener('window:resize')
  onResize() {
    this.screenWidth = window.innerWidth;
  }

  onMobileEdit(): boolean {
    return (this.screenWidth <= this.MIN_MD_WIDTH_EDIT);
  }

  col12Responsive(): string {
    return (this.screenWidth <= this.MAX_MD_WIDTH) ? "col-12" : "col-12";
  }

  dynamicAutocomplete(): string {
    if (this.onMobileEdit()) {
      return "p-autocomplete-panel";
    }
    return "p-autocomplete-panel-right";
  }

  dynamicButtonWidth(): string {
    return (this.screenWidth <= this.MIN_TWO_BUTTONS_DIALOG_WIDTH) ? "w-full" : "w-16rem";
  }

  dynamicInputTextWidth(): string {
    return (this.onMobileEdit()) ? "w-full h-2_5rem" : "w-23rem h-2rem";
  }

  justifyContentStyle(): string {
    return this.onMobileEdit() ? "justify-content-center" : "justify-content-start";
  }

  // Errors management & Design Border | Input
  showErrorInvalidMessage(type: HelperAddRowDialogComponent["f"]): boolean {
    return (type.errors !== null) && (type.errors !== undefined) && (type.dirty);
  }

  showErrorConfirmInvalidMessage(type: HelperAddRowDialogComponent["f"], notEquivalentType: string): boolean {
    return ((type.errors !== null && type.errors !== undefined && type.dirty) || (this.rowForm.errors !== null && this.rowForm.errors[notEquivalentType] !== null && this.rowForm.errors[notEquivalentType] && type.dirty));
  }

  showErrors(type: HelperAddRowDialogComponent["f"], classType: string): string {
    let inputTextStyle = "";

    switch (classType) {
      case 'borderRow':
        if (this.onMobileEdit()) {
          return "row";
        }
        return ((type.errors !== null) && (type.dirty)) ? "border-bottom-1 border-50" : "border-bottom-1 border-100";
      case 'borderInput':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 w-full p-0";
        }
        if ((type.errors !== null) && (type.dirty)) {
          inputTextStyle += " border-bottom-1 border-50";
        } else {
          inputTextStyle += " border-bottom-1 border-100";
        }
        return inputTextStyle;
      case 'label':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 p-1";
        }
        if ((type.errors !== null) && (type.dirty)) {
          inputTextStyle += " text-50";
        }
        return inputTextStyle;
      default:
        return inputTextStyle;
    }
  }

  showErrorsConfirmFields(type: HelperAddRowDialogComponent["f"], classType: string, notEquivalentType: string): string {
    let inputTextStyle = "";

    switch (classType) {
      case 'borderRow':
        if (this.onMobileEdit()) {
          return "row";
        }
        if ((type.errors !== null && type.dirty) || (this.rowForm.errors !== null && this.rowForm.errors[notEquivalentType] !== null && this.rowForm.errors[notEquivalentType] && type.dirty)) {
          inputTextStyle += " border-bottom-1 border-50";
        } else {
          inputTextStyle += " border-bottom-1 border-100";
        }
        return inputTextStyle;
      case 'borderInput':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 w-full p-0";
        }
        if ((type.errors !== null && type.dirty) || (this.rowForm.errors !== null && this.rowForm.errors[notEquivalentType] !== null && this.rowForm.errors[notEquivalentType] && type.dirty)) {
          inputTextStyle += " border-bottom-1 border-50";
        } else {
          inputTextStyle += " border-bottom-1 border-100";
        }
        return inputTextStyle;
      case 'label':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 p-1";
        }
        if ((type.errors !== null && type.dirty) || (this.rowForm.errors !== null && this.rowForm.errors[notEquivalentType] !== null && this.rowForm.errors[notEquivalentType] && type.dirty)) {
          inputTextStyle += " text-50";
        }
        return inputTextStyle;
      default:
        return inputTextStyle;
    }
  }

  showErrorRequiredMessage(type: HelperAddRowDialogComponent["f"]): boolean {
    return (type.errors !== null) && (type.dirty);
  }

  col4Responsive(fieldPosition: string): string {
    if (this.screenWidth >= this.MIN_WIDTH_COL4) {
      if (fieldPosition === 'left') {
        return "col-12 pl-6 pr-3";
      }
      if (fieldPosition === 'center') {
        return "col-12 px-5";
      }
      if (fieldPosition === 'right') {
        return "col-12 pl-3 pr-6";
      }
      return "col-12";
    } else {
      return (this.screenWidth >= this.MAX_MD_WIDTH) ? "col-12 px-12" : "col-12";
    }
  }
  dynamicInputTextFormat(classType: string): string {
    switch (classType) {
      case 'borderRow':
        if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
          return "row";
        }
        return "";
      case 'borderInput':
        if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
          return "col-12 w-full p-0";
        }
        return "";
      case 'label':
        if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
          return "col-12 p-1";
        }
        return "";
      case 'inputText':
        if (this.screenWidth <= this.MAX_MD_WIDTH) {
          if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
            return "w-full h-2_5rem"
          }
          return "w-20rem h-2rem";
        }
        return "w-13rem h-2rem";
      default:
        return "";
    }
  }
  marginResponsive(): string {
    return (this.screenWidth > this.MAX_MD_WIDTH) ? "mr-2_5rem" : "";
  }
}
